<template>
  <div
    class="vtk-container-wrapper"
    tabindex="0"
    @pointerenter="hover = true"
    @pointerleave="hover = false"
    @focusin="hover = true"
    @focusout="hover = false"
  >
    <div class="vtk-gutter">
      <v-btn
        color="white"
        icon
        size="medium"
        variant="text"
        @click="resetCamera"
      >
        <v-icon size="medium" class="py-1 view-control-icon"
          >mdi-camera-flip-outline</v-icon
        >
        <v-tooltip
          location="right"
          activator="parent"
          transition="slide-x-transition"
        >
          Reset Camera
        </v-tooltip>
      </v-btn>
      <slice-slider
        v-model="currentSlice"
        class="slice-slider"
        :min="sliceRange[0]"
        :max="sliceRange[1]"
        :step="1"
        :handle-height="20"
      />
    </div>
    <div class="vtk-container" data-testid="two-view-container">
      <v-progress-linear
        v-if="isImageLoading"
        indeterminate
        class="loading-indicator"
        height="2"
        color="grey"
      />
      <div class="vtk-sub-container">
        <vtk-slice-view
          class="vtk-view"
          ref="vtkView"
          data-testid="vtk-view vtk-two-view"
          :view-id="viewId"
          :image-id="currentImageID"
          :view-direction="viewDirection"
          :view-up="viewUp"
        >
          <vtk-slice-view-navigation />
          <vtk-slice-view-slicing-manipulator
            :view-id="viewId"
            :image-id="currentImageID"
            :view-direction="viewDirection"
          ></vtk-slice-view-slicing-manipulator>
          <vtk-slice-view-slicing-key-manipulator
            :view-id="viewId"
            :image-id="currentImageID"
            :view-direction="viewDirection"
          ></vtk-slice-view-slicing-key-manipulator>
          <vtk-slice-view-window-manipulator
            :view-id="viewId"
            :image-id="currentImageID"
            :manipulator-config="windowingManipulatorProps"
          ></vtk-slice-view-window-manipulator>
          <slice-viewer-overlay
            :view-id="viewId"
            :image-id="currentImageID"
          ></slice-viewer-overlay>
          <vtk-base-slice-representation
            ref="baseSliceRep"
            :view-id="viewId"
            :image-id="currentImageID"
            :axis="viewAxis"
          ></vtk-base-slice-representation>
          <vtk-segmentation-slice-representation
            v-for="maskId in segmentMaskIds"
            :key="`seg-${maskId}`"
            :view-id="viewId"
            :mask-id="maskId"
            :axis="viewAxis"
          ></vtk-segmentation-slice-representation>
          <template v-if="currentImageID">
            <vtk-layer-slice-representation
              v-for="layer in currentLayers"
              :key="`layer-${layer.id}`"
              ref="layerSliceReps"
              :view-id="viewId"
              :layer-id="layer.id"
              :parent-id="currentImageID"
              :axis="viewAxis"
            ></vtk-layer-slice-representation>
          </template>
          <crop-tool :view-id="viewId" :image-id="currentImageID" />
          <reference-lines
            v-if="referenceLinesVisible"
            :view-id="viewId"
            :image-id="currentImageID"
          />
          <CrosshairsWidget2D
            v-if="currentTool === Tools.Crosshairs"
            :view-id="viewId"
            :image-id="currentImageID"
            :view-direction="viewDirection"
          />
          <paint-tool
            :view-id="viewId"
            :image-id="currentImageID"
            :view-direction="viewDirection"
          />
          <polygon-tool
            :view-id="viewId"
            :image-id="currentImageID"
            :view-direction="viewDirection"
          />
          <ruler-tool
            :view-id="viewId"
            :image-id="currentImageID"
            :view-direction="viewDirection"
          />
          <rectangle-tool
            :view-id="viewId"
            :image-id="currentImageID"
            :view-direction="viewDirection"
          />
          <select-tool />
          <svg class="overlay-no-events">
            <bounding-rectangle :points="selectionPoints" />
          </svg>
          <scalar-probe
            :base-rep="baseSliceRep"
            :layer-reps="layerSliceReps"
          ></scalar-probe>
          <slot></slot>
        </vtk-slice-view>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, toRefs, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useCurrentImage } from '@/src/composables/useCurrentImage';
import VtkSliceView from '@/src/components/vtk/VtkSliceView.vue';
import { VtkViewApi } from '@/src/types/vtk-types';
import { Tools } from '@/src/store/tools/types';
import VtkBaseSliceRepresentation from '@/src/components/vtk/VtkBaseSliceRepresentation.vue';
import VtkSegmentationSliceRepresentation from '@/src/segmentation/rendering/VtkSegmentationSliceRepresentation.vue';
import { useSegmentationStore } from '@/src/segmentation/store';
import VtkLayerSliceRepresentation from '@/src/components/vtk/VtkLayerSliceRepresentation.vue';
import { useViewAnimationListener } from '@/src/composables/useViewAnimationListener';
import CropTool from '@/src/components/tools/crop/CropTool.vue';
import { ReferenceLines, useReferenceLinesStore } from '@/src/referenceLines';
import CrosshairsWidget2D from '@/src/components/tools/crosshairs/CrosshairsWidget2D.vue';
import PaintTool from '@/src/components/tools/paint/PaintTool.vue';
import PolygonTool from '@/src/components/tools/polygon/PolygonTool.vue';
import RulerTool from '@/src/components/tools/ruler/RulerTool.vue';
import RectangleTool from '@/src/components/tools/rectangle/RectangleTool.vue';
import SelectTool from '@/src/components/tools/SelectTool.vue';
import ScalarProbe from '@/src/components/tools/ScalarProbe.vue';
import BoundingRectangle from '@/src/components/tools/BoundingRectangle.vue';
import SliceSlider from '@/src/components/SliceSlider.vue';
import SliceViewerOverlay from '@/src/components/SliceViewerOverlay.vue';
import { useToolSelectionStore } from '@/src/store/tools/toolSelection';
import { useAnnotationToolStore, useToolStore } from '@/src/store/tools';
import {
  doesToolFrameMatchViewAxis,
  isToolVisible,
} from '@/src/composables/annotationTool';
import { useWebGLWatchdog } from '@/src/composables/useWebGLWatchdog';
import { useSliceConfig } from '@/src/composables/useSliceConfig';
import VtkSliceViewWindowManipulator from '@/src/components/vtk/VtkSliceViewWindowManipulator.vue';
import VtkSliceViewSlicingManipulator from '@/src/components/vtk/VtkSliceViewSlicingManipulator.vue';
import VtkSliceViewSlicingKeyManipulator from '@/src/components/vtk/VtkSliceViewSlicingKeyManipulator.vue';
import VtkSliceViewNavigation from '@/src/components/vtk/VtkSliceViewNavigation.vue';
import { useResetViewsEvents } from '@/src/components/tools/ResetViews.vue';
import { onVTKEvent } from '@/src/composables/onVTKEvent';
import { useViewStore } from '@/src/store/views';
import { ViewInfo2D } from '@/src/types/views';
import { get2DViewingVectors } from '@/src/utils/getViewingVectors';

type Props = {
  viewId: string;
};

const vtkView = ref<VtkViewApi>();
const baseSliceRep = ref();
const layerSliceReps = ref([]);

const props = defineProps<Props>();
const { viewId } = toRefs(props);

const viewStore = useViewStore();
const viewInfo = computed(() => viewStore.getView(viewId.value) as ViewInfo2D);

// base image
const {
  currentImageID,
  currentLayers,
  currentImageMetadata,
  currentImageData,
  isImageLoading,
} = useCurrentImage();

const viewAxis = computed(() => viewInfo.value.options.orientation);
const viewingVectors = computed(() => get2DViewingVectors(viewAxis.value));
const viewDirection = computed(() => viewingVectors.value.viewDirection);
const viewUp = computed(() => viewingVectors.value.viewUp);

const hover = ref(false);

function resetCamera() {
  vtkView.value?.resetCamera();
}

useResetViewsEvents().onClick(resetCamera);

useWebGLWatchdog(vtkView);
useViewAnimationListener(vtkView, viewId, '2D');

// active tool
const { currentTool } = storeToRefs(useToolStore());

const { visible: referenceLinesVisible } = storeToRefs(
  useReferenceLinesStore()
);

const { slice: currentSlice, range: sliceRange } = useSliceConfig(
  viewId,
  currentImageID
);

const windowingManipulatorProps = computed(() => {
  if (currentTool.value !== Tools.WindowLevel) return { button: -1 };
  return { button: 1 };
});

onVTKEvent(currentImageData, 'onModified', () => {
  vtkView.value?.requestRender();
});

// One representation per mask with storage on this image.
const segmentMaskIds = computed(() => {
  if (!currentImageID.value) return [];
  return useSegmentationStore().boundMaskIds(currentImageID.value);
});

// --- selection points --- //

const selectionStore = useToolSelectionStore();
const selectionPoints = computed(() => {
  return selectionStore.selection
    .map((sel) => {
      const store = useAnnotationToolStore(sel.type);
      return { store, tool: store.toolByID[sel.id] };
    })
    .filter(
      ({ tool }) =>
        tool.slice === currentSlice.value &&
        isToolVisible(tool) &&
        doesToolFrameMatchViewAxis(viewAxis, tool, currentImageMetadata)
    )
    .flatMap(({ store, tool }) => store.getPoints(tool.id));
});
</script>

<style scoped src="@/src/components/styles/vtk-view.css"></style>
<style scoped src="@/src/components/styles/utils.css"></style>
